import { FC, CSSProperties } from "react";
import { TestCaseStep } from "./TestCaseTypes";
import { SelectOption } from "./TestCaseSelectStyles";
import { getSubsystemIcon, TelecommandIcon, EmptyPageIcon, CloseIcon } from "./svgs/TestCaseSvgs";
import "../TestPlan/AllTestcasesList.scss";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";

interface TCStepsPopupProps {
    show: boolean;
    onClose: () => void;
    testCaseSteps: TestCaseStep[];
    selectedCommand?: SelectOption | null;
    busComponents: SelectOption[];
    title?: string;
    width?: number | string;
    height?: number | string;
}

const formatMs = (value: string | undefined): string => (
    value === undefined || value === "" ? "—" : `${value} ms`
);

const TCStepsPopup: FC<TCStepsPopupProps> = ({
    show,
    onClose,
    testCaseSteps,
    busComponents,
    title = "Telecommand",
    width = '45%',
    height = "calc(100% - 30px)",
}) => {
    const normalizeText = (value: string | undefined): string => (value ?? "").trim().toLowerCase();

    const parseCommandNameFromLabel = (label: string | undefined): string => {
        const raw = label ?? "";
        const parts = raw.split(" - ");
        return parts.length > 1 ? parts.slice(1).join(" - ") : raw;
    };

    const resolveSubsystem = (step: TestCaseStep): string => {
        const subsystemLabel = step.subsystem?.label ?? "";
        if (subsystemLabel && subsystemLabel !== "All") return subsystemLabel;

        const commandOption = step.teleData as SelectOption | null;
        if (!commandOption) return "—";

        const commandValue = commandOption.value as Record<string, unknown> | undefined;
        const commandSeq = commandValue?.sequenceData as Record<string, unknown> | undefined;
        const commandId = commandSeq?.commandId as number | undefined;
        const commandName = normalizeText((commandValue?.name as string | undefined) ?? parseCommandNameFromLabel(commandOption.label));
        const commandLabel = normalizeText(commandOption.label);

        for (const comp of busComponents) {
            if (comp.label === "All") continue;
            const cmds = comp.value as SelectOption[];
            if (!Array.isArray(cmds)) continue;

            const matched = cmds.some((cmd) => {
                const cmdValue = cmd.value as Record<string, unknown> | undefined;
                const cmdSeq = cmdValue?.sequenceData as Record<string, unknown> | undefined;
                const cmdId = cmdSeq?.commandId as number | undefined;
                const cmdName = normalizeText((cmdValue?.name as string | undefined) ?? parseCommandNameFromLabel(cmd.label));
                const cmdLabel = normalizeText(cmd.label);

                if (commandId != null && cmdId != null) {
                    if (commandId !== cmdId) return false;
                    if (commandName && cmdName) return commandName === cmdName;
                    return true;
                }
                if (commandName && cmdName) return commandName === cmdName;
                return !!commandLabel && commandLabel === cmdLabel;
            });

            if (matched) return comp.label;
        }

        return "—";
    };

    return (
        <div
            className={`tcsp-popup${show ? "" : " tcsp-popup--hidden"}`}
            style={{ '--tcsp-w': typeof width === 'number' ? `${width}px` : width, '--tcsp-h': typeof height === 'number' ? `${height}px` : height } as CSSProperties}
        >
            <div className="tcsp-header">
                <div className="tcsp-header__title-group">
                    <CustomToolTip title={title ?? ""} placement="top" className="max-w-50">
                        <span className="tcp-section-header__title tcsp-header__title">{title}</span>
                    </CustomToolTip>
                    <span className="tcd-preview__tc-badge tcsp-badge">
                        TC <span className="tcd-preview__tc-count">{testCaseSteps.length}</span>
                    </span>
                </div>
                <button className="tcsp-close-btn" onClick={onClose} title="Close">
                    <CloseIcon />
                </button>
            </div>

            {(!testCaseSteps || testCaseSteps.length === 0) && (
                <div className="atcl-empty-state">
                    <div className="atcl-empty-state__icon"><EmptyPageIcon /></div>
                    <div className="atcl-empty-state__text">
                        <span className="atcl-empty-state__title">No Preview</span>
                        <span className="atcl-empty-state__subtitle">Add Telecommands to view</span>
                    </div>
                </div>
            )}

            {testCaseSteps.length > 0 && (
                <div className="tcsp-content">
                    <div className="atcl-thead">
                        <div className="atcl-th atcl-th--icon"></div>
                        <div className="atcl-th atcl-th--name">Telecommand</div>
                        <div className="atcl-th atcl-th--sub">Subsystem</div>
                        <div className="atcl-th atcl-th--delay">Delay</div>
                        <div className="atcl-th atcl-th--delay">Timeout</div>
                    </div>
                    <div className="atcl-tbody">
                        {testCaseSteps.map((step, idx) => {
                            const displaySubsystem = resolveSubsystem(step);
                            return (
                                <div key={idx} className="atcl-row">
                                    <div className="atcl-td atcl-td--icon">
                                        <span className="atcl-name-text"><TelecommandIcon /></span>
                                    </div>
                                    <div className="atcl-td atcl-td--name">
                                        <span className="atcl-name-text">{step.teleData?.label ?? "—"}</span>
                                    </div>
                                    <div className="atcl-td atcl-td--sub">
                                        {displaySubsystem && displaySubsystem !== "—" ? (
                                            <CustomToolTip key={idx} title={displaySubsystem} placement="top">
                                                <span className="atcl-tag atcl-tag--outline">
                                                    {getSubsystemIcon(displaySubsystem)}
                                                    <span className="atcl-tag-text">{displaySubsystem}</span>
                                                </span>
                                            </CustomToolTip>
                                        ) : <span className="atcl-name-text">—</span>}
                                    </div>
                                    <div className="atcl-td atcl-td--delay">
                                        <CustomToolTip title={formatMs(step.delay)} placement="top" className="atcl-delay-tooltip">
                                            <span className="atcl-td--delay-text">{formatMs(step.delay)}</span>
                                        </CustomToolTip>
                                    </div>
                                    <div className="atcl-td atcl-td--delay">
                                        <CustomToolTip title={formatMs(step.timeout)} placement="top" className="atcl-delay-tooltip">
                                            <span className="atcl-td--delay-text">{formatMs(step.timeout)}</span>
                                        </CustomToolTip>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TCStepsPopup;
